import type { Step as RcStep } from '@sb/remote-control/types';
import type { Space, StepKind, Expression } from '@sb/routine-runner';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';
import {
  validateCodeExpression,
  validateMotionSpeed,
} from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

const permittedParentStepKinds = ['MoveArmToV2', 'Weld'];

class ArcRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Arc', 'Arc');
  }

  protected validate({
    args,
    routine,
    ctx,
    parents,
  }: CanonicalValidateArgs): void {
    const spaceItems = this.getSpaceItems(routine, ctx);
    this.checkParentKind(parents);
    const a = args as Extract<RcStep.Arguments, { argumentKind: 'Arc' }>;
    this.checkTcpOption(parents, a);
    this.checkTargets(a, spaceItems);
    this.checkMotionSpeed(a);
  }

  /**
   * Surface legacy `customLimits` percent-based fields as a deprecation
   * prompt.
   *
   * @throws {Error}
   */
  private checkMotionSpeed(
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
  ): void {
    validateMotionSpeed('Arc', args.motionSpeed);
  }

  /**
   * Ensure parent kind is allowed for Arc.
   * Throws ActionRequiredError when not inside MoveArm/Weld.
   *
   * @throws {ActionRequiredError}
   */
  private checkParentKind(
    parents: Array<{ stepKind: StepKind | string }>,
  ): void {
    const parentStepKind = parents?.[0]?.stepKind ?? '';

    if (!permittedParentStepKinds.includes(parentStepKind)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Arc steps may only be used inside a Move Arm or Weld step',
      });
    }
  }

  /**
   * Validate TCP option when parent is Weld.
   * Throws ActionRequiredError if invalid.
   *
   * @throws {ActionRequiredError}
   */
  private checkTcpOption(
    parents: Array<{ stepKind: StepKind | string }>,
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
  ): void {
    const parentStepKind = parents?.[0]?.stepKind ?? '';

    if (parentStepKind === 'Weld' && args.tcpOption !== 'auto') {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message:
          'TCP Option must be set to "auto" when used within a Weld step',
        fieldId: 'tcpOption',
      });
    }
  }

  /**
   * Validate both Arc targets.
   * May throw via validateTarget.
   *
   * @throws {ActionRequiredError}
   */
  private checkTargets(
    args: Extract<RcStep.Arguments, { argumentKind: 'Arc' }>,
    spaceItems: Space.Item[],
  ): void {
    for (let i = 0; i < 2; i += 1) {
      this.validateTarget(
        args.positionListIDs[i],
        args.positionListIndices[i],
        args.targets[i],
        args.targetKinds[i],
        args.expressions[i],
        spaceItems,
      );
    }
  }

  /**
   * Validate a single Arc target according to targetKind.
   * Throws ActionRequiredError when configuration is invalid.
   *
   * @throws {ActionRequiredError}
   */
  private validateTarget(
    positionListID: string | null,
    positionListIndex: number | null,
    target: { jointAngles?: unknown; pose?: unknown } | null,
    targetKind: 'singlePosition' | 'positionList' | 'expression',
    expression: Expression | null,
    spaceItems2: Space.Item[],
  ): void {
    const hasTarget = !!(target?.jointAngles || target?.pose);

    if (targetKind === 'singlePosition' && !hasTarget) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Joint angles or pose must be set',
        fieldId: 'jointAngles',
      });
    }

    if (targetKind === 'positionList') {
      if (positionListID == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'The space item must be set',
          fieldId: 'positionListID',
        });
      }

      const space = spaceItems2.find(
        (spaceItem) => spaceItem.id === positionListID,
      );

      if (space == null) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Choose a valid space item',
          fieldId: 'positionListID',
        });
      }

      // An untaught point list has no poses yet, so treat it as unset and
      // report it like a missing space item — clearer than an out-of-bounds
      // index error. Only operator-taught point lists are checked here.
      if (
        (space.kind === 'singlePosition' ||
          space.kind === 'freeformPositionList') &&
        space.positions.length === 0
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'The space item must be set',
          fieldId: 'positionListID',
        });
      }

      if (
        positionListIndex != null &&
        (positionListIndex < 0 || positionListIndex >= space.positions.length)
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Choose a valid index for the position list.',
          fieldId: 'positionListIndex',
        });
      }
    }

    if (targetKind === 'expression' && expression != null) {
      validateCodeExpression(expression, 'expression', 'Position expression');
    }
  }
}

export const ArcValidation: ValidationRule = new ArcRule().toValidationRule();
